/**
 * Site-wide click delegation for "scroll to this page's shop module" — shared by the buy entry points that replace email capture once sales open:
 *   · the buy button in sections/soulog-join-early.liquid (pre-order / general sale phase)
 *   · the buy pill in snippets/soulog-buy-pill.liquid
 *
 * Markup: <a href="product page URL" data-scroll-to-shop="optional shop module id">
 *   · If an id is given, scroll to that one; otherwise scroll to the first shop module on the page (the outer
 *     SoulogShop-<section.id> of sections/soulog-shop-module.liquid).
 *   · If the page has no shop module (another template reuses this section, or the shop module isn't rendered at all because sales haven't opened), don't intercept;
 *     let href navigate to the product page as usual — so href must be a working URL, not #.
 *
 * The same delegation also serves generic in-page jumps: <a href="#target-id" data-scroll-to="target-id">
 * (used by the numbered points in sections/soulog-home-cta.liquid to jump to the matching section above).
 * Optional data-scroll-item="n" asks a multi-item section to land on its nth item (soulog-home-record: SENSE / Recap, via its jumpTarget()).
 * If the target isn't on the page, don't intercept — the #anchor href is the no-JS fallback.
 *
 * Native smooth anchor scrolling is not used: this theme disables CSS scroll-behavior (it kills ScrollTrigger, see
 * tests/unit/scroll-behavior-guard.test.mjs), and the auto-hiding header reappears when scrolling up,
 * covering the top of the shop module, so we subtract the header height ourselves.
 */
(function () {
	if (window.__soulogScrollToShop) return;
	window.__soulogScrollToShop = true;

	function headerOffset() {
		var value = getComputedStyle(document.documentElement).getPropertyValue("--header-height");
		return parseFloat(value) || 0;
	}

	/**
	 * Smooth-scroll to computeTop(), then re-measure once the scroll settles and snap to the fresh value:
	 * sections passed on the way (lazy media, pin spacers) change height mid-scroll, so the target measured
	 * at click time can land hundreds of px off. Up to 3 corrections.
	 */
	function scrollToTop(computeTop) {
		var reduce = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
		var tries = 0;
		function go(behavior) {
			var top = computeTop();
			if (top == null) return;
			top = Math.max(0, top);
			window.scrollTo({ top: top, behavior: behavior });
			if (behavior === "auto" && Math.abs(window.pageYOffset - top) < 2) return;
			if (++tries > 3) return;
			var done = false;
			function settle() {
				if (done) return;
				done = true;
				window.removeEventListener("scrollend", settle);
				var fresh = computeTop();
				if (fresh != null && Math.abs(window.pageYOffset - Math.max(0, fresh)) >= 2) go("auto");
			}
			window.addEventListener("scrollend", settle);
			// Safari < 18 has no scrollend
			setTimeout(settle, behavior === "smooth" ? 1500 : 200);
		}
		go(reduce ? "auto" : "smooth");
	}

	function scrollToEl(el) {
		scrollToTop(function () {
			return el.getBoundingClientRect().top + window.pageYOffset - headerOffset();
		});
	}

	// The buy pill falls back to scrolling when checkout could not be created (so the error under the buy button is visible)
	window.__soulogScrollToShopEl = scrollToEl;

	document.addEventListener("click", function (event) {
		var trigger =
			event.target && event.target.closest ? event.target.closest("[data-scroll-to-shop], [data-scroll-to]") : null;
		if (!trigger) return;
		// Another handler already took over this click (the buy pill goes straight to checkout, see assets/soulog-buy-pill.js)
		if (event.defaultPrevented) return;
		// Modifier-key clicks (open in new tab / download, etc.) are left to the browser
		if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button > 0) return;

		var target;
		if (trigger.hasAttribute("data-scroll-to")) {
			target = document.getElementById(trigger.getAttribute("data-scroll-to"));
		} else {
			var id = trigger.getAttribute("data-scroll-to-shop");
			target = (id && document.getElementById(id)) || document.querySelector('[id^="SoulogShop-"]');
		}
		if (!target) return;

		event.preventDefault();
		// data-scroll-item="n" (1-based): ask a multi-item section where its nth item is (soulog-home-record's jumpTarget)
		var item = parseInt(trigger.getAttribute("data-scroll-item"), 10);
		var host = item > 0 ? target.querySelector("soulog-home-record") : null;
		if (host && typeof host.jumpTarget === "function") {
			scrollToTop(function () {
				return host.jumpTarget(item - 1, headerOffset());
			});
			return;
		}
		scrollToEl(target);
	});
})();
